<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { shallowRef } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import OutingChecklist from "@/components/outings/OutingChecklist.vue"
import OutingItemPicker from "@/components/outings/OutingItemPicker.vue"
import OutingReadiness from "@/components/outings/OutingReadiness.vue"
import { useNavbar } from "@/composables/useNavbar"
import { ICON } from "@/lib/icons"
import {
  checkInOutingOutingItemPath,
  checkOutOutingPath,
  cloneOutingPath,
  closeOutingPath,
  outingContainerVerificationPath,
  outingOutingItemPath,
  outingOutingItemsPath,
  packingOutingOutingItemPath,
  verificationOutingOutingItemPath,
} from "@/routes"
import type { ItemSummary, OutingDetail, OutingItemSummary } from "@/types/models"

type AvailableItem = ItemSummary & { is_container: boolean }
type VerificationResult = { present: boolean } | { quantity: number }

const props = defineProps<{
  outing: OutingDetail
  available_items: AvailableItem[]
}>()

const { title } = useNavbar({ title: props.outing.name })
const addingItem = shallowRef(false)
const removingItem = shallowRef(false)
const removeTarget = shallowRef<OutingDetail["outing_items"][number]>()
const removeModalOpen = shallowRef(false)
const removeContents = shallowRef(true)
const changingItemId = shallowRef<string>()

function addItem(itemId: string, includeContents: boolean) {
  addingItem.value = true
  router.post(
    outingOutingItemsPath(props.outing.id),
    { item_id: itemId, include_contents: includeContents },
    { preserveScroll: true, onFinish: () => (addingItem.value = false) },
  )
}

function removeItem(outingItemId: string) {
  const item = props.outing.outing_items.find((candidate) => candidate.id === outingItemId)
  if (item?.is_container) {
    removeTarget.value = item
    removeContents.value = true
    removeModalOpen.value = true
    return
  }

  router.delete(outingOutingItemPath(props.outing.id, outingItemId), { preserveScroll: true })
}

function confirmRemove() {
  if (!removeTarget.value) return

  removingItem.value = true
  router.delete(outingOutingItemPath(props.outing.id, removeTarget.value.id), {
    data: { include_contents: removeContents.value },
    preserveScroll: true,
    onFinish: () => {
      removingItem.value = false
      removeModalOpen.value = false
    },
  })
}

function checkOut() {
  router.post(checkOutOutingPath(props.outing.id), {}, { preserveScroll: true })
}

function checkIn(outingItemId: string) {
  router.post(
    checkInOutingOutingItemPath(props.outing.id, outingItemId),
    {},
    { preserveScroll: true },
  )
}

function verifyItem(item: OutingItemSummary, result: VerificationResult) {
  changingItemId.value = item.id
  const options = {
    preserveScroll: true,
    onFinish: () => (changingItemId.value = undefined),
  }

  if (item.home_container_id) {
    router.patch(
      outingContainerVerificationPath(props.outing.id, item.home_container_id),
      { verification: { results: [{ item: item.item_id, ...result }] } },
      options,
    )
    return
  }

  router.patch(verificationOutingOutingItemPath(props.outing.id, item.id), result, options)
}

function packItem(item: OutingItemSummary, packed: boolean) {
  changingItemId.value = item.id
  router.patch(
    packingOutingOutingItemPath(props.outing.id, item.id),
    { packed },
    {
      preserveScroll: true,
      onFinish: () => (changingItemId.value = undefined),
    },
  )
}

function close() {
  router.post(closeOutingPath(props.outing.id))
}

function clone() {
  router.post(cloneOutingPath(props.outing.id))
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <header class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-2">
          <h2 class="text-highlighted truncate text-lg font-semibold">{{ outing.name }}</h2>
          <UBadge color="neutral" variant="subtle">{{ outing.status }}</UBadge>
        </div>
        <p class="text-muted mt-1 text-sm">
          {{ outing.location || "No location" }} · {{ outing.returned_count }}/{{
            outing.item_count
          }}
          returned
        </p>
      </div>
      <UButton
        v-if="outing.status === 'closed'"
        label="Clone"
        :icon="ICON.duplicate"
        color="neutral"
        variant="soft"
        size="sm"
        @click="clone"
      />
    </header>

    <OutingItemPicker
      v-if="outing.status === 'draft'"
      :items="available_items"
      :processing="addingItem"
      @add="addItem"
    />

    <OutingReadiness
      v-if="outing.status === 'draft'"
      :kit-ready="outing.kit_ready"
      :outstanding-prep-count="outing.outstanding_prep_count"
      :unverified-item-count="outing.unverified_item_count"
      :prep-entries="outing.prep_entries"
    />

    <OutingChecklist
      :items="outing.outing_items"
      :outing-status="outing.status"
      :processing-item-id="changingItemId"
      @check-in="checkIn"
      @pack="packItem"
      @remove="removeItem"
      @verify="verifyItem"
    />

    <div class="flex justify-end gap-2">
      <UButton
        v-if="outing.status === 'draft'"
        label="Check out all items"
        :icon="ICON.outing"
        :disabled="!outing.kit_ready"
        @click="checkOut"
      />
      <UButton
        v-else-if="outing.status === 'out'"
        label="Close outing"
        :icon="ICON.check"
        @click="close"
      />
    </div>
  </NarrowPage>

  <DeleteConfirmModal
    :open="removeModalOpen"
    :title="`Remove ${removeTarget?.item_name ?? 'container'} from outing?`"
    :loading="removingItem"
    action-label="Remove"
    :action-icon="ICON.delete"
    @update:open="removeModalOpen = $event"
    @confirm="confirmRemove"
  >
    <div class="flex flex-col gap-4">
      <p class="text-muted text-sm">
        Inventory and container contents will not be changed. This only removes outing membership.
      </p>
      <UCheckbox
        v-model="removeContents"
        label="Also remove all current contents from this outing"
      />
    </div>
  </DeleteConfirmModal>
</template>
